import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import {
  Calculator, Save, FolderOpen, Plus, Trash2, Copy, Download,
  ChevronDown, ChevronUp, TrendingUp, DollarSign,
  FileText, BarChart3, X, Columns, Share2, Target, AlertTriangle, Layers, HelpCircle
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { HowToUseGuide } from '../components/HowToUseGuide';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v || 0);
const fmtPct = (v, dec = 2) => `${((v || 0) * 100).toFixed(dec)}%`;

// ── Reusable Inputs ──────────────────────────────────────
function CurrencyInput({ label, value, onChange, className = '' }) {
  const display = value ? Number(value).toLocaleString('en-US') : '';
  const handleChange = (e) => {
    const raw = e.target.value.replace(/[^0-9.]/g, '');
    onChange(raw === '' ? '' : parseFloat(raw) || 0);
  };
  return (
    <div className={className}>
      {label && <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</label>}
      <div className="relative mt-0.5">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
        <Input value={display} onChange={handleChange} className="pl-6 h-8 rounded-sm text-sm font-mono" />
      </div>
    </div>
  );
}

function PercentInput({ label, value, onChange }) {
  return (
    <div>
      {label && <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</label>}
      <div className="relative mt-0.5">
        <Input type="number" step="0.1" value={value || ''} onChange={e => onChange(parseFloat(e.target.value) || 0)} className="pr-6 h-8 rounded-sm text-sm font-mono" />
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">%</span>
      </div>
    </div>
  );
}

function ReadOnlyField({ label, value, accent }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</label>
      <p className={`font-mono text-sm font-semibold mt-0.5 ${accent ? 'text-emerald-700' : ''}`}>{value}</p>
    </div>
  );
}

function StatLine({ label, value, bold, accent }) {
  return (
    <div className="flex items-center justify-between text-xs py-0.5">
      <span className={`text-muted-foreground ${bold ? 'font-semibold text-foreground' : ''}`}>{label}</span>
      <span className={`font-mono ${bold ? 'font-bold' : ''} ${accent ? 'text-emerald-700 font-bold' : ''}`}>{value}</span>
    </div>
  );
}

// ── Tier Editor ──────────────────────────────────────────
function TierEditor({ tiers, setTiers, label }) {
  const addTier = () => {
    const last = tiers[tiers.length - 1];
    setTiers([...tiers, { days_start: (last?.days_end || 0) + 1, days_end: '', lf_split: 50, investor_split: 50 }]);
  };
  const removeTier = (i) => setTiers(tiers.filter((_, idx) => idx !== i));
  const updateTier = (i, f, v) => {
    const updated = [...tiers];
    updated[i] = { ...updated[i], [f]: v };
    if (f === 'lf_split') { const clamped = Math.max(0, Math.min(100, parseFloat(v) || 0)); updated[i].lf_split = clamped; updated[i].investor_split = 100 - clamped; }
    if (f === 'investor_split') { const clamped = Math.max(0, Math.min(100, parseFloat(v) || 0)); updated[i].investor_split = clamped; updated[i].lf_split = 100 - clamped; }
    setTiers(updated);
  };
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-5 gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        <span>Start Day</span><span>End Day</span><span>LF Split %</span><span>Partner %</span><span></span>
      </div>
      {/* Composite key based on row position + content. When tiers are
          reordered/removed mid-list, React preserves the input focus on the
          row the user is actually editing instead of remounting siblings. */}
      {tiers.map((t, i) => (
        <div key={`tier-${i}-${t.days_start}-${t.days_end ?? 'inf'}`} className="grid grid-cols-5 gap-1">
          <Input type="number" value={t.days_start} onChange={e => updateTier(i, 'days_start', parseInt(e.target.value) || 0)} className="h-7 text-xs rounded-sm font-mono" />
          <Input type="number" value={t.days_end} onChange={e => updateTier(i, 'days_end', e.target.value === '' ? '' : parseInt(e.target.value) || 0)} className="h-7 text-xs rounded-sm font-mono" placeholder="∞" />
          <Input type="number" value={t.lf_split} onChange={e => updateTier(i, 'lf_split', parseFloat(e.target.value) || 0)} className="h-7 text-xs rounded-sm font-mono" />
          <Input type="number" value={t.investor_split} onChange={e => updateTier(i, 'investor_split', parseFloat(e.target.value) || 0)} className="h-7 text-xs rounded-sm font-mono" />
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => removeTier(i)}><Trash2 className="w-3 h-3" /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" className="h-7 text-[10px] rounded-sm w-full" onClick={addTier}><Plus className="w-3 h-3 mr-1" /> Add Tier</Button>
    </div>
  );
}

// ── Results Table ────────────────────────────────────────
function ResultsTable({ results, title, investorCapital }) {
  if (!results.length) return null;
  return (
    <div>
      <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">{title}</h4>
      <div className="border rounded-sm overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/50 border-b">
            <tr>
              <th className="text-left px-2.5 py-1.5 font-medium">Tier</th>
              <th className="text-right px-2.5 py-1.5 font-medium">LF %</th>
              <th className="text-right px-2.5 py-1.5 font-medium">LF Profit</th>
              <th className="text-right px-2.5 py-1.5 font-medium">Partner %</th>
              <th className="text-right px-2.5 py-1.5 font-medium">Partner Profit</th>
              <th className="text-right px-2.5 py-1.5 font-medium">CoC</th>
              <th className="text-right px-2.5 py-1.5 font-medium">Ann. ROI</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r, i) => (
              <tr key={`${r.days_start}-${r.days_end ?? 'inf'}-${i}`} className="border-b last:border-0">
                <td className="px-2.5 py-1.5 font-mono">{r.days_start}-{r.days_end === '' || r.days_end == null ? '+' : r.days_end} days</td>
                <td className="px-2.5 py-1.5 text-right font-mono">{r.lf_split}%</td>
                <td className="px-2.5 py-1.5 text-right font-mono">{fmt(r.lf_profit)}</td>
                <td className="px-2.5 py-1.5 text-right font-mono">{r.investor_split}%</td>
                <td className="px-2.5 py-1.5 text-right font-mono">{fmt(r.investor_profit)}</td>
                <td className="px-2.5 py-1.5 text-right font-mono text-emerald-700">{fmtPct(r.coc)}</td>
                <td className="px-2.5 py-1.5 text-right font-mono text-blue-600">{r.annualized_roi != null ? fmtPct(r.annualized_roi) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="rounded-sm">
      <CardHeader className="py-2.5 px-4 cursor-pointer select-none" onClick={() => setOpen(!open)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Icon className="w-4 h-4 text-muted-foreground" /><CardTitle className="text-sm">{title}</CardTitle></div>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </CardHeader>
      {open && <CardContent className="px-4 pb-4 pt-0">{children}</CardContent>}
    </Card>
  );
}

// ── Default State ────────────────────────────────────────
const DEFAULT_DEAL = {
  purchase_price: '', survey: '', soil: '', other_dd: '',
  purchase_closing_costs: '', improvements: '',
  sale_price: '', sale_closing_costs: '', sale_commission_pct: 6,
};
const DEFAULT_FUNDING = {
  lf_pct_purchase: 0, lf_pct_dd: 0, lf_pct_improvements: 0, lf_pct_closing: 50,
  preferred_return: 0,
};
// Scenario A — standard 5-tier investor schedule (40/45/50/55/60).
// Investor % goes UP as hold time grows (incentivizes patient capital).
const DEFAULT_TIERS_A = [
  { days_start: 1,   days_end: 180,  lf_split: 60, investor_split: 40 },
  { days_start: 181, days_end: 270,  lf_split: 55, investor_split: 45 },
  { days_start: 271, days_end: 365,  lf_split: 50, investor_split: 50 },
  { days_start: 366, days_end: 456,  lf_split: 45, investor_split: 55 },
  { days_start: 457, days_end: '',   lf_split: 40, investor_split: 60 },
];
// Scenario B — less generous 4-tier alternative offer (25/35/45/55).
const DEFAULT_TIERS_B = [
  { days_start: 1,   days_end: 180,  lf_split: 75, investor_split: 25 },
  { days_start: 181, days_end: 270,  lf_split: 65, investor_split: 35 },
  { days_start: 271, days_end: 365,  lf_split: 55, investor_split: 45 },
  { days_start: 366, days_end: '',   lf_split: 45, investor_split: 55 },
];

const STORAGE_KEY = 'lf_deal_analyses';

function loadFromStorage() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch { return []; }
}

function saveToStorage(analyses) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(analyses));
}

// ── Main Component ───────────────────────────────────────
export default function PublicDealAnalysis() {
  const [analyses, setAnalyses] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [name, setName] = useState('Untitled Analysis');
  const [notes, setNotes] = useState('');
  const [deal, setDeal] = useState({ ...DEFAULT_DEAL });
  const [funding, setFunding] = useState({ ...DEFAULT_FUNDING });
  const [optionATiers, setOptionATiers] = useState([...DEFAULT_TIERS_A]);
  const [optionBTiers, setOptionBTiers] = useState([...DEFAULT_TIERS_B]);
  const [showSaved, setShowSaved] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [compareId, setCompareId] = useState(null);
  const [acreage, setAcreage] = useState('');
  const [isSubdivide, setIsSubdivide] = useState(false);
  const [parcels, setParcels] = useState([]);
  const [isDirty, setIsDirty] = useState(false);

  // Mark dirty on any input change
  useEffect(() => { setIsDirty(true); }, [name, notes, deal, funding, optionATiers, optionBTiers, acreage, isSubdivide, parcels]);

  useEffect(() => { setAnalyses(loadFromStorage()); }, []);

  // Load shared analysis from URL param
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const shared = params.get('d');
      if (shared) {
        const data = JSON.parse(atob(shared));
        if (data.name) setName(data.name);
        if (data.deal) setDeal({ ...DEFAULT_DEAL, ...data.deal });
        if (data.funding) setFunding({ ...DEFAULT_FUNDING, ...data.funding });
        if (data.option_a_tiers?.length) setOptionATiers(data.option_a_tiers);
        if (data.option_b_tiers?.length) setOptionBTiers(data.option_b_tiers);
        if (data.notes) setNotes(data.notes);
        if (data.acreage) setAcreage(data.acreage);
        if (data.is_subdivide) setIsSubdivide(data.is_subdivide);
        if (data.parcels?.length) setParcels(data.parcels);
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
        toast.success('Shared analysis loaded');
      }
    } catch (e) { console.debug('Invalid shared analysis data — ignoring:', e?.message); }
  }, []);

  // ── Calculations ─────────────────────────────────────
  const calcs = useMemo(() => {
    const pp = parseFloat(deal.purchase_price) || 0;
    const survey = parseFloat(deal.survey) || 0;
    const soil = parseFloat(deal.soil) || 0;
    const otherDD = parseFloat(deal.other_dd) || 0;
    const totalDD = survey + soil + otherDD;
    const purchaseClosing = parseFloat(deal.purchase_closing_costs) || 0;
    const improvements = parseFloat(deal.improvements) || 0;
    const totalAcquisitionCost = pp + totalDD + purchaseClosing + improvements;

    const sp = parseFloat(deal.sale_price) || 0;
    const saleClosing = parseFloat(deal.sale_closing_costs) || 0;
    const saleCommPct = parseFloat(deal.sale_commission_pct) || 0;
    const saleCommission = sp * (saleCommPct / 100);
    const grossSalesRevenue = sp - saleClosing - saleCommission;
    const grossProfit = grossSalesRevenue - totalAcquisitionCost;
    const margin = sp > 0 ? grossProfit / sp : 0;

    const lfPurchase = pp * ((parseFloat(funding.lf_pct_purchase) || 0) / 100);
    const lfDD = totalDD * ((parseFloat(funding.lf_pct_dd) || 0) / 100);
    const lfImprovements = improvements * ((parseFloat(funding.lf_pct_improvements) || 0) / 100);
    const lfClosing = purchaseClosing * ((parseFloat(funding.lf_pct_closing) || 0) / 100);
    const lfTotalInvestment = lfPurchase + lfDD + lfImprovements + lfClosing;
    const investorTotalInvestment = totalAcquisitionCost - lfTotalInvestment;

    return {
      pp, survey, soil, otherDD, totalDD, purchaseClosing, improvements, totalAcquisitionCost,
      sp, saleClosing, saleCommPct, saleCommission, grossSalesRevenue, grossProfit, margin,
      lfPurchase, lfDD, lfImprovements, lfClosing, lfTotalInvestment, investorTotalInvestment,
      pricePerAcrePurchase: (parseFloat(acreage) || 0) > 0 ? pp / parseFloat(acreage) : 0,
      pricePerAcreSale: (parseFloat(acreage) || 0) > 0 ? sp / parseFloat(acreage) : 0,
    };
  }, [deal, funding, acreage]);

  const computeTierResults = useCallback((tiers) => {
    return tiers.map(t => {
      const lfSplit = parseFloat(t.lf_split) || 0;
      const invSplit = parseFloat(t.investor_split) || 0;
      const lfProfit = calcs.grossProfit * (lfSplit / 100);
      const invProfit = calcs.grossProfit * (invSplit / 100);
      const coc = calcs.investorTotalInvestment > 0 ? invProfit / calcs.investorTotalInvestment : 0;
      const dEnd = t.days_end === '' || t.days_end == null ? null : parseInt(t.days_end);
      const annROI = dEnd && dEnd > 0 ? coc * (365 / dEnd) : null;
      return { days_start: parseInt(t.days_start) || 0, days_end: t.days_end, lf_split: lfSplit, investor_split: invSplit, lf_profit: lfProfit, investor_profit: invProfit, coc, annualized_roi: annROI };
    });
  }, [calcs]);

  const optionAResults = useMemo(() => computeTierResults(optionATiers), [optionATiers, computeTierResults]);
  const optionBResults = useMemo(() => computeTierResults(optionBTiers), [optionBTiers, computeTierResults]);

  // ── Subdivide ──────────────────────────────────────────
  const addParcel = () => {
    const n = parcels.length + 1;
    setParcels(prev => [...prev, { name: `Parcel ${n}`, acres: '', price_per_acre: '', sale_price: '', closing_costs: '', commission_pct: '', days_held: '', is_jv: true }]);
  };
  const removeParcel = (i) => setParcels(prev => prev.filter((_, idx) => idx !== i));
  const updateParcel = (index, field, value) => {
    setParcels(prev => prev.map((p, i) => {
      if (i !== index) return p;
      const u = { ...p, [field]: value };
      const a = parseFloat(u.acres) || 0;
      if (field === 'price_per_acre' && a > 0) u.sale_price = Math.round((parseFloat(value) || 0) * a);
      else if (field === 'sale_price' && a > 0) u.price_per_acre = Math.round(((parseFloat(value) || 0) / a) * 100) / 100;
      else if (field === 'acres' && parseFloat(u.price_per_acre) > 0) u.sale_price = Math.round(parseFloat(u.price_per_acre) * (parseFloat(value) || 0));
      return u;
    }));
  };

  const parcelResults = useMemo(() => {
    if (!isSubdivide || !parcels.length) return [];
    const totalAcres = parseFloat(acreage) || 0;
    const mainClosing = parseFloat(deal.sale_closing_costs) || 0;
    return parcels.map(p => {
      const pAcres = parseFloat(p.acres) || 0;
      const pSP = parseFloat(p.sale_price) || 0;
      const pCommPct = p.commission_pct !== '' ? parseFloat(p.commission_pct) : calcs.saleCommPct;
      const pComm = pSP * (pCommPct / 100);
      const costRatio = totalAcres > 0 ? pAcres / totalAcres : 0;
      const pClosing = p.closing_costs !== '' ? (parseFloat(p.closing_costs) || 0) : costRatio * mainClosing;
      const pRevenue = pSP - pComm - pClosing;
      const costAlloc = costRatio * calcs.totalAcquisitionCost;
      const gp = pRevenue - costAlloc;
      const days = parseInt(p.days_held) || 0;
      let tier = optionATiers.find(t => {
        const s = parseInt(t.days_start) || 0;
        const e = t.days_end === '' || t.days_end == null ? Infinity : parseInt(t.days_end);
        return days >= s && days <= e;
      });
      if (!tier && optionATiers.length > 0) tier = optionATiers[0];
      let lfPct = 100, partnerPct = 0;
      if (p.is_jv && calcs.investorTotalInvestment > 0 && tier) {
        lfPct = parseFloat(tier.lf_split) || 0;
        partnerPct = parseFloat(tier.investor_split) || 0;
      }
      return { ...p, pAcres, revenue: pRevenue, commission: pComm, commPct: pCommPct, closingCosts: pClosing, costAlloc, grossProfit: gp, lfPct, partnerPct, lfProfit: gp * (lfPct / 100), partnerProfit: gp * (partnerPct / 100), tierLabel: tier ? `${tier.days_start}-${tier.days_end || '+'}d` : '—', hasDays: (parseInt(p.days_held) || 0) > 0 };
    });
  }, [isSubdivide, parcels, acreage, calcs, optionATiers, deal.sale_closing_costs]);

  const subdivSummary = useMemo(() => {
    if (!parcelResults.length) return null;
    return {
      totalAcres: parcelResults.reduce((s, p) => s + p.pAcres, 0),
      totalSalePrice: parcelResults.reduce((s, p) => s + (parseFloat(p.sale_price) || 0), 0),
      totalClosing: parcelResults.reduce((s, p) => s + (p.closingCosts || 0), 0),
      totalRevenue: parcelResults.reduce((s, p) => s + p.revenue, 0),
      totalGP: parcelResults.reduce((s, p) => s + p.grossProfit, 0),
      totalLF: parcelResults.reduce((s, p) => s + p.lfProfit, 0),
      totalPartner: parcelResults.reduce((s, p) => s + p.partnerProfit, 0),
    };
  }, [parcelResults]);

  // Scenario B parcel results for comparison
  const parcelResultsB = useMemo(() => {
    if (!isSubdivide || !parcels.length) return [];
    const totalAcres = parseFloat(acreage) || 0;
    const mainClosing = parseFloat(deal.sale_closing_costs) || 0;
    return parcels.map(p => {
      const pAcres = parseFloat(p.acres) || 0;
      const pSP = parseFloat(p.sale_price) || 0;
      const pCommPct = p.commission_pct !== '' ? parseFloat(p.commission_pct) : calcs.saleCommPct;
      const pComm = pSP * (pCommPct / 100);
      const costRatio = totalAcres > 0 ? pAcres / totalAcres : 0;
      const pClosing = p.closing_costs !== '' ? (parseFloat(p.closing_costs) || 0) : costRatio * mainClosing;
      const pRevenue = pSP - pComm - pClosing;
      const costAlloc = costRatio * calcs.totalAcquisitionCost;
      const gp = pRevenue - costAlloc;
      const days = parseInt(p.days_held) || 0;
      let tier = optionBTiers.find(t => {
        const s = parseInt(t.days_start) || 0;
        const e = t.days_end === '' || t.days_end == null ? Infinity : parseInt(t.days_end);
        return days >= s && days <= e;
      });
      if (!tier && optionBTiers.length > 0) tier = optionBTiers[0];
      let lfPct = 100, partnerPct = 0;
      if (p.is_jv && calcs.investorTotalInvestment > 0 && tier) {
        lfPct = parseFloat(tier.lf_split) || 0;
        partnerPct = parseFloat(tier.investor_split) || 0;
      }
      return { ...p, pAcres, revenue: pRevenue, costAlloc, grossProfit: gp, lfPct, partnerPct, lfProfit: gp * (lfPct / 100), partnerProfit: gp * (partnerPct / 100), tierLabel: tier ? `${tier.days_start}-${tier.days_end || '+'}d` : '—' };
    });
  }, [isSubdivide, parcels, acreage, calcs, optionBTiers, deal.sale_closing_costs]);

  const subdivSummaryB = useMemo(() => {
    if (!parcelResultsB.length) return null;
    return {
      totalLF: parcelResultsB.reduce((s, p) => s + p.lfProfit, 0),
      totalPartner: parcelResultsB.reduce((s, p) => s + p.partnerProfit, 0),
      totalGP: parcelResultsB.reduce((s, p) => s + p.grossProfit, 0),
    };
  }, [parcelResultsB]);

  // ── Tier Validation ────────────────────────────────────
  const tierWarnings = useMemo(() => {
    const check = (tiers, label) => {
      const warnings = [];
      for (let i = 0; i < tiers.length; i++) {
        const s = parseInt(tiers[i].days_start) || 0;
        const e = tiers[i].days_end === '' || tiers[i].days_end == null ? Infinity : parseInt(tiers[i].days_end);
        if (e !== Infinity && e < s) warnings.push(`${label} Tier ${i + 1}: End day (${e}) is before start day (${s})`);
        if (i > 0) {
          const prevE = tiers[i - 1].days_end === '' || tiers[i - 1].days_end == null ? Infinity : parseInt(tiers[i - 1].days_end);
          if (prevE !== Infinity && s > prevE + 1) warnings.push(`${label}: Gap between Tier ${i} (ends ${prevE}) and Tier ${i + 1} (starts ${s})`);
          if (prevE !== Infinity && s <= prevE) warnings.push(`${label}: Overlap between Tier ${i} (ends ${prevE}) and Tier ${i + 1} (starts ${s})`);
        }
      }
      return warnings;
    };
    return [...check(optionATiers, 'Scenario A'), ...check(optionBTiers, 'Scenario B')];
  }, [optionATiers, optionBTiers]);

  // ── Parcel Acres Validation ─────────────────────────────
  const parcelAcresTotal = useMemo(() => parcels.reduce((s, p) => s + (parseFloat(p.acres) || 0), 0), [parcels]);
  const totalAcresNum = parseFloat(acreage) || 0;
  const acresMismatch = isSubdivide && parcels.length > 0 && totalAcresNum > 0 && Math.abs(parcelAcresTotal - totalAcresNum) > 0.01;

  const setDealField = (f, v) => setDeal(prev => ({ ...prev, [f]: v }));
  const setFundingField = (f, v) => setFunding(prev => ({ ...prev, [f]: v }));

  // ── Goal Seek ──────────────────────────────────────────
  const [goalType, setGoalType] = useState('margin');
  const [goalValue, setGoalValue] = useState('');
  const [solveFor, setSolveFor] = useState('sale_price');
  const [goalTierIndex, setGoalTierIndex] = useState(0);

  const goalResult = useMemo(() => {
    const target = parseFloat(goalValue) || 0;
    if (target <= 0) return null;

    const tac = calcs.totalAcquisitionCost;
    const scc = parseFloat(deal.sale_closing_costs) || 0;
    const scPct = calcs.saleCommPct / 100;
    const sp = calcs.sp;
    const totalDD = calcs.totalDD;
    const purchaseClosing = calcs.purchaseClosing;
    const improvements = calcs.improvements;

    const lfPctPP = (parseFloat(funding.lf_pct_purchase) || 0) / 100;
    const lfPctDD = (parseFloat(funding.lf_pct_dd) || 0) / 100;
    const lfPctImp = (parseFloat(funding.lf_pct_improvements) || 0) / 100;
    const lfPctClose = (parseFloat(funding.lf_pct_closing) || 0) / 100;
    const selectedTier = optionATiers[goalTierIndex] || optionATiers[0];
    const partnerSplitPct = selectedTier ? (parseFloat(selectedTier.investor_split) || 0) / 100 : 0.4;

    if (solveFor === 'sale_price') {
      if (goalType === 'margin') {
        const m = target / 100;
        const denom = 1 - scPct - m;
        if (denom <= 0) return { error: 'Target margin too high for current commission rate' };
        return { value: (scc + tac) / denom, label: 'Required Sale Price' };
      }
      let requiredGP;
      if (goalType === 'profit') {
        requiredGP = target;
      } else {
        const partnerInv = calcs.investorTotalInvestment;
        if (partnerInv <= 0) return { error: 'No partner investment configured' };
        if (partnerSplitPct <= 0) return { error: 'No partner split configured' };
        requiredGP = (target / 100) * partnerInv / partnerSplitPct;
      }
      const denom = 1 - scPct;
      if (denom <= 0) return { error: 'Sale commission is 100% or more' };
      return { value: (requiredGP + scc + tac) / denom, label: 'Required Sale Price' };

    } else {
      const netSaleProceeds = sp * (1 - scPct) - scc;
      if (goalType === 'profit') {
        const maxPP = netSaleProceeds - totalDD - purchaseClosing - improvements - target;
        return { value: Math.max(0, maxPP), label: 'Maximum Purchase Price' };
      } else if (goalType === 'margin') {
        const requiredGP = sp * (target / 100);
        const maxPP = netSaleProceeds - totalDD - purchaseClosing - improvements - requiredGP;
        return { value: Math.max(0, maxPP), label: 'Maximum Purchase Price' };
      } else {
        const C = totalDD * (1 - lfPctDD) + improvements * (1 - lfPctImp) + purchaseClosing * (1 - lfPctClose);
        const R = netSaleProceeds - totalDD - purchaseClosing - improvements;
        const t = target / 100;
        const s = partnerSplitPct;
        const a = 1 - lfPctPP;
        if (s <= 0) return { error: 'No partner split configured' };
        const denom = s + t * a;
        if (denom <= 0) return { error: 'Cannot solve — target too high' };
        const maxPP = (R * s - t * C) / denom;
        return { value: Math.max(0, maxPP), label: 'Maximum Purchase Price' };
      }
    }
  }, [goalType, goalValue, solveFor, calcs, deal, funding, optionATiers, goalTierIndex]);

  const goalSummary = useMemo(() => {
    if (!goalResult || goalResult.error) return null;
    const rv = goalResult.value;
    const newSP = solveFor === 'sale_price' ? rv : calcs.sp;
    const newPP = solveFor === 'purchase_price' ? rv : calcs.pp;
    const scPct = calcs.saleCommPct / 100;
    const scc = parseFloat(deal.sale_closing_costs) || 0;
    const newTAC = newPP + calcs.totalDD + calcs.purchaseClosing + calcs.improvements;
    const newGP = (newSP - scc - newSP * scPct) - newTAC;
    const newMargin = newSP > 0 ? newGP / newSP : 0;
    const newLFInv = newPP * ((parseFloat(funding.lf_pct_purchase) || 0) / 100) + calcs.totalDD * ((parseFloat(funding.lf_pct_dd) || 0) / 100) + calcs.improvements * ((parseFloat(funding.lf_pct_improvements) || 0) / 100) + calcs.purchaseClosing * ((parseFloat(funding.lf_pct_closing) || 0) / 100);
    const newPartnerInv = newTAC - newLFInv;
    const tier = optionATiers[goalTierIndex] || optionATiers[0];
    const pSplit = tier ? (parseFloat(tier.investor_split) || 0) / 100 : 0.4;
    const lSplit = tier ? (parseFloat(tier.lf_split) || 0) / 100 : 0.6;
    return { gp: newGP, margin: newMargin, coc: newPartnerInv > 0 ? (newGP * pSplit) / newPartnerInv : 0, partnerProfit: newGP * pSplit, lfProfit: newGP * lSplit, partnerInv: newPartnerInv };
  }, [goalResult, solveFor, calcs, deal, funding, optionATiers, goalTierIndex]);

  const applyGoalResult = () => {
    if (!goalResult || goalResult.error) return;
    if (solveFor === 'sale_price') setDealField('sale_price', Math.round(goalResult.value));
    else setDealField('purchase_price', Math.round(goalResult.value));
    toast.success(`Applied ${goalResult.label}: ${fmt(goalResult.value)}`);
  };

  const loadAnalysis = (a) => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard and load?')) return;
    setActiveId(a.id);
    setName(a.name);
    setNotes(a.notes || '');
    setDeal(a.deal || { ...DEFAULT_DEAL });
    setFunding(a.funding || { ...DEFAULT_FUNDING });
    setOptionATiers(a.option_a_tiers?.length ? a.option_a_tiers : [...DEFAULT_TIERS_A]);
    setOptionBTiers(a.option_b_tiers?.length ? a.option_b_tiers : [...DEFAULT_TIERS_B]);
    setAcreage(a.acreage || '');
    setIsSubdivide(a.is_subdivide || false);
    setParcels(a.parcels || []);
    setShowSaved(false);
    setIsDirty(false);
    toast.success(`Loaded "${a.name}"`);
  };

  const handleNew = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard and start new?')) return;
    setActiveId(null);
    setName('Untitled Analysis');
    setNotes('');
    setDeal({ ...DEFAULT_DEAL });
    setFunding({ ...DEFAULT_FUNDING });
    setOptionATiers([...DEFAULT_TIERS_A]);
    setOptionBTiers([...DEFAULT_TIERS_B]);
    setAcreage('');
    setIsSubdivide(false);
    setParcels([]);
    setIsDirty(false);
  };

  const handleSave = () => {
    const now = new Date().toISOString();
    const payload = { name, notes, deal, funding, option_a_tiers: optionATiers, option_b_tiers: optionBTiers, acreage, is_subdivide: isSubdivide, parcels, updated_at: now };
    let updated;
    if (activeId) {
      updated = analyses.map(a => a.id === activeId ? { ...a, ...payload } : a);
      toast.success('Analysis saved');
    } else {
      const newId = 'local_' + Date.now();
      updated = [{ ...payload, id: newId, created_at: now }, ...analyses];
      setActiveId(newId);
      toast.success('Analysis created');
    }
    setAnalyses(updated);
    saveToStorage(updated);
    setIsDirty(false);
  };

  const handleDelete = (id) => {
    const updated = analyses.filter(a => a.id !== id);
    setAnalyses(updated);
    saveToStorage(updated);
    if (activeId === id) handleNew();
    if (compareId === id) setCompareId(null);
    toast.success('Deleted');
  };

  const handleDuplicate = (id) => {
    const source = analyses.find(a => a.id === id);
    if (!source) return;
    const now = new Date().toISOString();
    const copy = { ...JSON.parse(JSON.stringify(source)), id: 'local_' + Date.now(), name: source.name + ' (Copy)', created_at: now, updated_at: now };
    const updated = [copy, ...analyses];
    setAnalyses(updated);
    saveToStorage(updated);
    toast.success('Duplicated');
  };

  const handleShare = async () => {
    try {
      const data = { name, deal, funding, option_a_tiers: optionATiers, option_b_tiers: optionBTiers, notes, acreage, is_subdivide: isSubdivide, parcels };
      const encoded = btoa(JSON.stringify(data));
      const url = `${window.location.origin}${window.location.pathname}?d=${encoded}`;
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Share link copied to clipboard');
      if (url.length > 8000) toast.info('This link is very long due to parcel data. Some browsers may truncate it.', { duration: 5000 });
    } catch { toast.error('Failed to generate share link'); }
  };

  const handleDownloadPDF = () => {
    try {
      const doc = new jsPDF();
      const pw = doc.internal.pageSize.getWidth();
      const FOREST = [26, 47, 35]; const TERRA = [198, 142, 93]; const LIGHT = [245, 245, 240];

      // Header
      doc.setFillColor(...FOREST); doc.rect(0, 0, pw, 32, 'F');
      doc.setFillColor(...TERRA); doc.rect(0, 32, pw, 1.5, 'F');
      doc.setTextColor(255, 255, 255); doc.setFontSize(18); doc.text(name || 'Deal Analysis', 14, 17);
      doc.setFontSize(10); doc.text(`Land Finder  |  ${new Date().toLocaleDateString()}`, 14, 26);

      let y = 42;
      doc.setTextColor(0, 0, 0);

      const summaryRowStyle = { fillColor: LIGHT, fontStyle: 'bold' };

      // Acquisition Costs
      doc.setFontSize(12); doc.setFont(undefined, 'bold'); doc.text('Acquisition Costs', 14, y); y += 8;
      const acqBody = [
        ['Purchase Price', fmt(calcs.pp)],
        ['Survey', fmt(calcs.survey)], ['Soil', fmt(calcs.soil)], ['Other DD', fmt(calcs.otherDD)],
        ['Total Due Diligence', fmt(calcs.totalDD)],
        ['Purchase Closing Costs', fmt(calcs.purchaseClosing)],
        ['Improvements', fmt(calcs.improvements)],
        [{ content: 'Total Acquisition Cost', styles: summaryRowStyle }, { content: fmt(calcs.totalAcquisitionCost), styles: summaryRowStyle }],
      ];
      if ((parseFloat(acreage) || 0) > 0) {
        acqBody.unshift(['Total Acreage', `${Number(acreage).toFixed(2)} acres`]);
        acqBody.push(['Purchase Price / Acre', fmt(calcs.pricePerAcrePurchase)]);
      }
      autoTable(doc, {
        startY: y, margin: { left: 14, right: 14 }, theme: 'grid', headStyles: { fillColor: FOREST, fontSize: 8 }, bodyStyles: { fontSize: 8 },
        head: [['Item', 'Amount']],
        body: acqBody,
      });
      y = doc.lastAutoTable.finalY + 10;

      // Sale & Revenue
      doc.setFontSize(12); doc.text('Sale & Revenue', 14, y); y += 8;
      const saleBody = [
        ['Sale Price', fmt(calcs.sp)],
        ['Sale Closing Costs', `-${fmt(calcs.saleClosing)}`],
        [`Sale Commission (${calcs.saleCommPct}%)`, `-${fmt(calcs.saleCommission)}`],
        [{ content: 'Gross Sales Revenue', styles: summaryRowStyle }, { content: fmt(calcs.grossSalesRevenue), styles: summaryRowStyle }],
        [{ content: 'Gross Profit', styles: { fillColor: [220, 237, 225], fontStyle: 'bold' } }, { content: fmt(calcs.grossProfit), styles: { fillColor: [220, 237, 225], fontStyle: 'bold' } }],
        ['Margin', fmtPct(calcs.margin)],
      ];
      if ((parseFloat(acreage) || 0) > 0 && calcs.sp > 0) saleBody.push(['Sale Price / Acre', fmt(calcs.pricePerAcreSale)]);
      autoTable(doc, {
        startY: y, margin: { left: 14, right: 14 }, theme: 'grid', headStyles: { fillColor: FOREST, fontSize: 8 }, bodyStyles: { fontSize: 8 },
        head: [['Item', 'Amount']],
        body: saleBody,
      });
      y = doc.lastAutoTable.finalY + 10;

      // Funding
      doc.setFontSize(12); doc.text('Funding Structure', 14, y); y += 8;
      autoTable(doc, {
        startY: y, margin: { left: 14, right: 14 }, theme: 'grid', headStyles: { fillColor: FOREST, fontSize: 8 }, bodyStyles: { fontSize: 8 },
        head: [['Category', 'LF %', 'LF Amount', 'Partner Amount']],
        body: [
          ['Purchase Price', `${funding.lf_pct_purchase || 0}%`, fmt(calcs.lfPurchase), fmt(calcs.pp - calcs.lfPurchase)],
          ['DD Costs', `${funding.lf_pct_dd || 0}%`, fmt(calcs.lfDD), fmt(calcs.totalDD - calcs.lfDD)],
          ['Improvements', `${funding.lf_pct_improvements || 0}%`, fmt(calcs.lfImprovements), fmt(calcs.improvements - calcs.lfImprovements)],
          ['Closing Costs', `${funding.lf_pct_closing || 0}%`, fmt(calcs.lfClosing), fmt(calcs.purchaseClosing - calcs.lfClosing)],
          [
            { content: 'Total', styles: summaryRowStyle },
            { content: '', styles: summaryRowStyle },
            { content: fmt(calcs.lfTotalInvestment), styles: summaryRowStyle },
            { content: fmt(calcs.investorTotalInvestment), styles: summaryRowStyle },
          ],
        ],
      });
      y = doc.lastAutoTable.finalY + 10;

      // Tiers
      const tierBody = (results) => results.map(r => [
        `${r.days_start}-${r.days_end === '' || r.days_end == null ? '+' : r.days_end} days`,
        `${r.lf_split}%`, fmt(r.lf_profit), `${r.investor_split}%`, fmt(r.investor_profit), fmtPct(r.coc), r.annualized_roi != null ? fmtPct(r.annualized_roi) : '—',
      ]);

      if (optionAResults.length) {
        if (y > 240) { doc.addPage(); y = 20; }
        doc.setFontSize(12); doc.text('Scenario A — Profit Split', 14, y); y += 8;
        autoTable(doc, {
          startY: y, margin: { left: 14, right: 14 }, theme: 'grid', headStyles: { fillColor: FOREST, fontSize: 7 }, bodyStyles: { fontSize: 7 },
          head: [['Tier', 'LF %', 'LF Profit', 'Partner %', 'Partner Profit', 'CoC', 'Ann. ROI']],
          body: tierBody(optionAResults),
        });
        y = doc.lastAutoTable.finalY + 10;
      }
      if (optionBResults.length) {
        if (y > 240) { doc.addPage(); y = 20; }
        doc.setFontSize(12); doc.text('Scenario B — Profit Split', 14, y); y += 8;
        autoTable(doc, {
          startY: y, margin: { left: 14, right: 14 }, theme: 'grid', headStyles: { fillColor: FOREST, fontSize: 7 }, bodyStyles: { fontSize: 7 },
          head: [['Tier', 'LF %', 'LF Profit', 'Partner %', 'Partner Profit', 'CoC', 'Ann. ROI']],
          body: tierBody(optionBResults),
        });
        y = doc.lastAutoTable.finalY + 10;
      }

      // Subdivide Breakdown
      if (isSubdivide && parcelResults.length > 0) {
        if (y > 200) { doc.addPage(); y = 20; }
        doc.setFontSize(12); doc.setFont(undefined, 'bold'); doc.text('Subdivide Analysis', 14, y); y += 8;
        autoTable(doc, {
          startY: y, margin: { left: 14, right: 14 }, theme: 'grid', headStyles: { fillColor: TERRA, fontSize: 7, textColor: [255, 255, 255] }, bodyStyles: { fontSize: 7 },
          head: [['Parcel', 'Acres', '$/Acre', 'Sale Price', 'Closing', 'Revenue', 'Cost Alloc.', 'Profit', 'LF Profit', 'Partner', 'Tier']],
          body: [
            ...parcelResults.map(r => [
              r.name + (r.is_jv ? '' : ' (No JV)'), String(r.pAcres), fmt(r.pAcres > 0 ? (parseFloat(r.sale_price) || 0) / r.pAcres : 0),
              fmt(parseFloat(r.sale_price) || 0), fmt(r.closingCosts || 0), fmt(r.revenue), fmt(r.costAlloc), fmt(r.grossProfit), fmt(r.lfProfit), fmt(r.partnerProfit), r.tierLabel,
            ]),
            [
              { content: 'Total', styles: summaryRowStyle }, { content: String(subdivSummary?.totalAcres?.toFixed(2) || ''), styles: summaryRowStyle }, { content: '', styles: summaryRowStyle },
              { content: fmt(subdivSummary?.totalSalePrice || 0), styles: summaryRowStyle }, { content: fmt(subdivSummary?.totalClosing || 0), styles: summaryRowStyle },
              { content: fmt(subdivSummary?.totalRevenue || 0), styles: summaryRowStyle },
              { content: fmt(calcs.totalAcquisitionCost), styles: summaryRowStyle }, { content: fmt(subdivSummary?.totalGP || 0), styles: summaryRowStyle },
              { content: fmt(subdivSummary?.totalLF || 0), styles: summaryRowStyle }, { content: fmt(subdivSummary?.totalPartner || 0), styles: summaryRowStyle },
              { content: '', styles: summaryRowStyle },
            ],
          ],
        });
        y = doc.lastAutoTable.finalY + 10;
      }

      // Scenario Comparison
      if (isSubdivide && parcelResults.length > 0 && parcelResultsB.length > 0) {
        if (y > 180) { doc.addPage(); y = 20; }
        doc.setFontSize(12); doc.setFont(undefined, 'bold'); doc.text('Scenario Comparison', 14, y); y += 8;
        const compBody = parcelResults.map((rA, i) => {
          const rB = parcelResultsB[i];
          return [
            rA.name + (rA.is_jv ? '' : ' (No JV)'), String(rA.pAcres), String(rA.days_held || '—'),
            rA.tierLabel, fmt(rA.lfProfit), fmt(rA.partnerProfit),
            rB?.tierLabel || '—', fmt(rB?.lfProfit || 0), fmt(rB?.partnerProfit || 0),
          ];
        });
        const aTotalLF = subdivSummary?.totalLF || 0;
        const bTotalLF = subdivSummaryB?.totalLF || 0;
        const aTotalP = subdivSummary?.totalPartner || 0;
        const bTotalP = subdivSummaryB?.totalPartner || 0;
        compBody.push([
          { content: 'Totals', styles: summaryRowStyle }, { content: '', styles: summaryRowStyle }, { content: '', styles: summaryRowStyle },
          { content: '', styles: summaryRowStyle }, { content: fmt(aTotalLF), styles: summaryRowStyle }, { content: fmt(aTotalP), styles: summaryRowStyle },
          { content: '', styles: summaryRowStyle }, { content: fmt(bTotalLF), styles: summaryRowStyle }, { content: fmt(bTotalP), styles: summaryRowStyle },
        ]);
        autoTable(doc, {
          startY: y, margin: { left: 14, right: 14 }, theme: 'grid', bodyStyles: { fontSize: 7 },
          headStyles: { fillColor: FOREST, fontSize: 7, textColor: [255, 255, 255] },
          head: [['Parcel', 'Acres', 'Days', 'A Tier', 'A LF Profit', 'A Partner', 'B Tier', 'B LF Profit', 'B Partner']],
          body: compBody,
          columnStyles: { 3: { fillColor: [240, 245, 240] }, 4: { fillColor: [240, 245, 240] }, 5: { fillColor: [240, 245, 240] }, 6: { fillColor: [252, 245, 238] }, 7: { fillColor: [252, 245, 238] }, 8: { fillColor: [252, 245, 238] } },
        });
        y = doc.lastAutoTable.finalY + 4;
        const winner = aTotalLF >= bTotalLF ? 'Scenario A' : 'Scenario B';
        const diff = Math.abs(aTotalLF - bTotalLF);
        doc.setFontSize(9); doc.setFont(undefined, 'bold');
        doc.text(`LF Profit Advantage: ${winner} by ${fmt(diff)}`, 14, y + 4);
        y += 12;
      }

      // Notes
      if (notes && notes.trim()) {
        if (y > 250) { doc.addPage(); y = 20; }
        doc.setFontSize(12); doc.setFont(undefined, 'bold'); doc.text('Notes', 14, y); y += 7;
        doc.setFontSize(9); doc.setFont(undefined, 'normal');
        const lines = doc.splitTextToSize(notes, pw - 28);
        doc.text(lines, 14, y);
        y += lines.length * 4.5;
      }

      // Footer on all pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8); doc.setTextColor(150, 150, 150);
        doc.text(`Land Finder  |  Confidential`, 14, doc.internal.pageSize.getHeight() - 10);
        doc.text(`Page ${i} of ${pageCount}`, pw - 14, doc.internal.pageSize.getHeight() - 10, { align: 'right' });
      }

      doc.save(`${(name || 'analysis').replace(/\s+/g, '_')}.pdf`);
      toast.success('PDF downloaded');
    } catch (e) { console.error(e); toast.error('PDF generation failed'); }
  };

  const compareAnalysis = useMemo(() => analyses.find(a => a.id === compareId), [analyses, compareId]);
  const compareCalcs = useMemo(() => {
    if (!compareAnalysis) return null;
    const d = compareAnalysis.deal || {};
    const f = compareAnalysis.funding || {};
    const pp = parseFloat(d.purchase_price) || 0;
    const tDD = (parseFloat(d.survey) || 0) + (parseFloat(d.soil) || 0) + (parseFloat(d.other_dd) || 0);
    const pClose = parseFloat(d.purchase_closing_costs) || 0;
    const impr = parseFloat(d.improvements) || 0;
    const totalAcq = pp + tDD + pClose + impr;
    const sp = parseFloat(d.sale_price) || 0;
    const sClose = parseFloat(d.sale_closing_costs) || 0;
    const sCommPct = parseFloat(d.sale_commission_pct) || 0;
    const sComm = sp * (sCommPct / 100);
    const gsr = sp - sClose - sComm;
    const gp = gsr - totalAcq;
    const np = gp;
    const lfInv = pp * ((parseFloat(f.lf_pct_purchase) || 0) / 100) + tDD * ((parseFloat(f.lf_pct_dd) || 0) / 100) + impr * ((parseFloat(f.lf_pct_improvements) || 0) / 100) + pClose * ((parseFloat(f.lf_pct_closing) || 0) / 100);
    const invInv = totalAcq - lfInv;
    return { pp, sp, totalAcq, gsr, gp, np, lfInv, invInv, margin: sp > 0 ? gp / sp : 0 };
  }, [compareAnalysis]);

  return (
    <div className="min-h-screen bg-[#fafaf8]">
      <Toaster richColors position="top-right" />
      {/* Header Bar */}
      <header className="bg-[#1A2F23] text-white px-4 sm:px-6 py-3 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#C68E5D] rounded-sm flex items-center justify-center">
            <Calculator className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-sm font-bold tracking-wide">Land Finder</h1>
            <p className="text-[10px] text-white/60">Deal Analysis Calculator</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="rounded-sm gap-1.5 h-8 text-xs text-white/80 hover:text-white hover:bg-white/10" onClick={() => setShowGuide(true)} data-testid="pub-help-btn">
            <HelpCircle className="w-3.5 h-3.5" /> How to Use
          </Button>
          <Button variant="ghost" size="sm" className="rounded-sm gap-1.5 h-8 text-xs text-white/80 hover:text-white hover:bg-white/10" onClick={() => setShowSaved(true)} data-testid="pub-open-saved-btn">
            <FolderOpen className="w-3.5 h-3.5" /> Saved ({analyses.length})
          </Button>
          <Button variant="ghost" size="sm" className="rounded-sm gap-1.5 h-8 text-xs text-white/80 hover:text-white hover:bg-white/10" onClick={handleNew} data-testid="pub-new-btn">
            <Plus className="w-3.5 h-3.5" /> New
          </Button>
          <Button variant="ghost" size="sm" className="rounded-sm gap-1.5 h-8 text-xs text-white/80 hover:text-white hover:bg-white/10" onClick={handleDownloadPDF} data-testid="pub-pdf-btn">
            <Download className="w-3.5 h-3.5" /> PDF
          </Button>
          <Button variant="ghost" size="sm" className="rounded-sm gap-1.5 h-8 text-xs text-white/80 hover:text-white hover:bg-white/10" onClick={handleShare} data-testid="pub-share-btn">
            <Share2 className="w-3.5 h-3.5" /> Share
          </Button>
          <Button size="sm" className="rounded-sm gap-1.5 h-8 text-xs bg-[#C68E5D] hover:bg-[#b07d50] text-white" onClick={handleSave} data-testid="pub-save-btn">
            <Save className="w-3.5 h-3.5" /> {activeId ? 'Save' : 'Save New'}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        {/* Analysis Name */}
        <div className="mb-4">
          <div className="flex items-center gap-2 group">
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              onFocus={e => { if (e.target.value === 'Untitled Analysis') e.target.select(); }}
              className={`text-lg font-semibold h-9 border-0 border-b-2 border-dashed shadow-none rounded-none px-1 focus-visible:ring-0 bg-transparent transition-colors focus:border-[#C68E5D] ${name === 'Untitled Analysis' ? 'text-muted-foreground/60 border-muted-foreground/25' : 'border-transparent hover:border-muted-foreground/30'}`}
              placeholder="Enter deal name..."
              data-testid="pub-analysis-name"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* LEFT: Inputs */}
          <div className="space-y-3">
            <Section title="Acquisition Costs" icon={DollarSign}>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <CurrencyInput label="Purchase Price" value={deal.purchase_price} onChange={v => setDealField('purchase_price', v)} />
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Total Acreage</label>
                    <input type="number" value={acreage} onChange={e => setAcreage(e.target.value)} placeholder="0" className="w-full mt-1 px-2.5 py-1.5 border rounded-sm text-sm" data-testid="pub-acreage-input" />
                  </div>
                </div>
                {(parseFloat(acreage) || 0) > 0 && calcs.pp > 0 && (
                  <ReadOnlyField label="Purchase Price / Acre" value={fmt(calcs.pricePerAcrePurchase)} />
                )}
                <Separator />
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Due Diligence</p>
                <div className="grid grid-cols-3 gap-2">
                  <CurrencyInput label="Survey" value={deal.survey} onChange={v => setDealField('survey', v)} />
                  <CurrencyInput label="Soil" value={deal.soil} onChange={v => setDealField('soil', v)} />
                  <CurrencyInput label="Other DD" value={deal.other_dd} onChange={v => setDealField('other_dd', v)} />
                </div>
                <ReadOnlyField label="Total Due Diligence" value={fmt(calcs.totalDD)} />
                <div className="grid grid-cols-2 gap-2">
                  <CurrencyInput label="Purchase Closing Costs" value={deal.purchase_closing_costs} onChange={v => setDealField('purchase_closing_costs', v)} />
                  <CurrencyInput label="Improvements" value={deal.improvements} onChange={v => setDealField('improvements', v)} />
                </div>
                <ReadOnlyField label="Total Acquisition Cost" value={fmt(calcs.totalAcquisitionCost)} accent />
              </div>
            </Section>

            <Section title="Sale & Revenue" icon={TrendingUp}>
              <div className="space-y-3">
                <CurrencyInput label="Estimated Sale Price" value={deal.sale_price} onChange={v => setDealField('sale_price', v)} />
                <div className="grid grid-cols-2 gap-2">
                  <CurrencyInput label="Sale Closing Costs" value={deal.sale_closing_costs} onChange={v => setDealField('sale_closing_costs', v)} />
                  <PercentInput label="Sale Commission %" value={deal.sale_commission_pct} onChange={v => setDealField('sale_commission_pct', v)} />
                </div>
                <ReadOnlyField label={`Sale Commission (${calcs.saleCommPct}%)`} value={fmt(calcs.saleCommission)} />
                <ReadOnlyField label="Gross Sales Revenue" value={fmt(calcs.grossSalesRevenue)} />
                <ReadOnlyField label="Gross Profit" value={fmt(calcs.grossProfit)} accent />
                {(parseFloat(acreage) || 0) > 0 && calcs.sp > 0 && (
                  <ReadOnlyField label="Sale Price / Acre" value={fmt(calcs.pricePerAcreSale)} />
                )}
              </div>
            </Section>

            <Section title="Funding Structure" icon={BarChart3}>
              <div className="space-y-3">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Land Finder Funding %</p>
                <div className="grid grid-cols-2 gap-2">
                  <PercentInput label="% of Purchase Price" value={funding.lf_pct_purchase} onChange={v => setFundingField('lf_pct_purchase', v)} />
                  <ReadOnlyField label="LF Amount" value={fmt(calcs.lfPurchase)} />
                  <PercentInput label="% of DD Costs" value={funding.lf_pct_dd} onChange={v => setFundingField('lf_pct_dd', v)} />
                  <ReadOnlyField label="LF Amount" value={fmt(calcs.lfDD)} />
                  <PercentInput label="% of Improvements" value={funding.lf_pct_improvements} onChange={v => setFundingField('lf_pct_improvements', v)} />
                  <ReadOnlyField label="LF Amount" value={fmt(calcs.lfImprovements)} />
                  <PercentInput label="% of Closing Costs" value={funding.lf_pct_closing} onChange={v => setFundingField('lf_pct_closing', v)} />
                  <ReadOnlyField label="LF Amount" value={fmt(calcs.lfClosing)} />
                </div>
                <Separator />
                <div className="grid grid-cols-2 gap-2">
                  <ReadOnlyField label="Total LF Investment" value={fmt(calcs.lfTotalInvestment)} />
                  <ReadOnlyField label="Total Partner Investment" value={fmt(calcs.investorTotalInvestment)} accent />
                </div>
              </div>
            </Section>

            {tierWarnings.length > 0 && (
              <div className="p-2.5 rounded-sm bg-red-50 border border-red-200" data-testid="tier-warnings">
                <p className="text-[11px] font-semibold text-red-800 mb-1"><AlertTriangle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Tier Configuration Issues</p>
                {tierWarnings.map((w, i) => <p key={`${w}-${i}`} className="text-[10px] text-red-700">{w}</p>)}
              </div>
            )}

            <Section title="Scenario A — Profit Split Tiers" icon={BarChart3}>
              {calcs.investorTotalInvestment === 0 && optionATiers.some(t => (parseFloat(t.investor_split) || 0) > 0) && (
                <div className="mb-3 p-2.5 rounded-sm bg-amber-50 border border-amber-200 flex items-start sm:items-center justify-between gap-2 flex-col sm:flex-row">
                  <p className="text-[11px] text-amber-800">
                    <AlertTriangle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                    LF is funding 100% — but partner still receives a profit share below.
                  </p>
                  <button onClick={() => setOptionATiers(prev => prev.map(t => ({ ...t, lf_split: 100, investor_split: 0 })))} className="px-2.5 py-1 text-[10px] font-medium bg-amber-100 hover:bg-amber-200 rounded-sm border border-amber-300 text-amber-900 whitespace-nowrap transition-colors" data-testid="pub-scen-a-full-lf-btn">
                    Set LF to 100%
                  </button>
                </div>
              )}
              <TierEditor tiers={optionATiers} setTiers={setOptionATiers} label="Scenario A" />
            </Section>

            <Section title="Scenario B — Profit Split Tiers" icon={BarChart3} defaultOpen={false}>
              {calcs.investorTotalInvestment === 0 && optionBTiers.some(t => (parseFloat(t.investor_split) || 0) > 0) && (
                <div className="mb-3 p-2.5 rounded-sm bg-amber-50 border border-amber-200 flex items-start sm:items-center justify-between gap-2 flex-col sm:flex-row">
                  <p className="text-[11px] text-amber-800">
                    <AlertTriangle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                    LF is funding 100% — but partner still receives a profit share below.
                  </p>
                  <button onClick={() => setOptionBTiers(prev => prev.map(t => ({ ...t, lf_split: 100, investor_split: 0 })))} className="px-2.5 py-1 text-[10px] font-medium bg-amber-100 hover:bg-amber-200 rounded-sm border border-amber-300 text-amber-900 whitespace-nowrap transition-colors" data-testid="pub-scen-b-full-lf-btn">
                    Set LF to 100%
                  </button>
                </div>
              )}
              <TierEditor tiers={optionBTiers} setTiers={setOptionBTiers} label="Scenario B" />
            </Section>

            <Section title="Subdivide Analysis" icon={Layers} defaultOpen={isSubdivide}>
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={isSubdivide} onChange={e => { setIsSubdivide(e.target.checked); if (e.target.checked && !parcels.length) addParcel(); }} className="rounded border-gray-300" data-testid="pub-subdivide-toggle" />
                  <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Enable Subdivide Mode</span>
                </label>
                {isSubdivide && (
                  <>
                    {acresMismatch && (
                      <div className="p-2 rounded-sm bg-amber-50 border border-amber-200" data-testid="pub-acres-warning">
                        <p className="text-[10px] text-amber-800"><AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5" />
                          Parcel acres ({parcelAcresTotal.toFixed(2)}) {parcelAcresTotal > totalAcresNum ? 'exceed' : 'are less than'} total acreage ({totalAcresNum.toFixed(2)}). Cost allocation may be inaccurate.
                        </p>
                      </div>
                    )}
                    <div className="overflow-x-auto -mx-2">
                      <table className="w-full text-xs min-w-[750px]">
                        <thead>
                          <tr className="border-b bg-muted/30">
                            <th className="text-left p-1.5 font-medium text-muted-foreground">Name</th>
                            <th className="text-left p-1.5 font-medium text-muted-foreground">Acres</th>
                            <th className="text-left p-1.5 font-medium text-muted-foreground">$/Acre</th>
                            <th className="text-left p-1.5 font-medium text-muted-foreground">Sale Price</th>
                            <th className="text-left p-1.5 font-medium text-muted-foreground">Closing $</th>
                            <th className="text-left p-1.5 font-medium text-muted-foreground">Comm%</th>
                            <th className="text-left p-1.5 font-medium text-muted-foreground">Days</th>
                            <th className="text-center p-1.5 font-medium text-muted-foreground">JV</th>
                            <th className="w-6"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {parcels.map((p, i) => (
                            <tr key={`parcel-${i}-${p.name || ''}`} className="border-b">
                              <td className="p-1"><input value={p.name} onChange={e => updateParcel(i, 'name', e.target.value)} className="w-20 px-1.5 py-1 border rounded-sm text-xs" /></td>
                              <td className="p-1"><input type="number" value={p.acres} onChange={e => updateParcel(i, 'acres', e.target.value)} className="w-14 px-1.5 py-1 border rounded-sm text-xs" placeholder="0" /></td>
                              <td className="p-1"><input type="number" value={p.price_per_acre} onChange={e => updateParcel(i, 'price_per_acre', e.target.value)} className="w-20 px-1.5 py-1 border rounded-sm text-xs" placeholder="0" /></td>
                              <td className="p-1"><input type="number" value={p.sale_price} onChange={e => updateParcel(i, 'sale_price', e.target.value)} className="w-24 px-1.5 py-1 border rounded-sm text-xs" placeholder="0" /></td>
                              <td className="p-1"><input type="number" value={p.closing_costs} onChange={e => updateParcel(i, 'closing_costs', e.target.value)} className="w-16 px-1.5 py-1 border rounded-sm text-xs" placeholder="auto" /></td>
                              <td className="p-1"><input type="number" value={p.commission_pct} onChange={e => updateParcel(i, 'commission_pct', e.target.value)} className="w-12 px-1.5 py-1 border rounded-sm text-xs" placeholder={String(calcs.saleCommPct || 6)} /></td>
                              <td className="p-1"><input type="number" value={p.days_held} onChange={e => updateParcel(i, 'days_held', e.target.value)} className="w-14 px-1.5 py-1 border rounded-sm text-xs" placeholder="0" /></td>
                              <td className="p-1 text-center"><input type="checkbox" checked={p.is_jv} onChange={e => updateParcel(i, 'is_jv', e.target.checked)} className="rounded border-gray-300" /></td>
                              <td className="p-1"><button onClick={() => removeParcel(i)} className="p-0.5 text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <button onClick={addParcel} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="pub-add-parcel-btn">
                      <Plus className="w-3 h-3" /> Add Parcel
                    </button>
                  </>
                )}
              </div>
            </Section>

            <Section title="Notes" icon={FileText} defaultOpen={false}>
              <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Deal notes, assumptions, risk factors..." rows={3} className="text-sm rounded-sm" />
            </Section>

            <Section title="Goal Seek" icon={Target} defaultOpen={false}>
              <div className="space-y-3">
                {isSubdivide && (
                  <div className="p-2 rounded-sm bg-blue-50 border border-blue-200" data-testid="pub-goal-seek-subdiv-note">
                    <p className="text-[10px] text-blue-800"><AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5" />
                      Goal Seek solves based on the single-sale scenario. In Subdivide mode, each parcel has its own sale price — apply results to the main deal inputs before subdividing.
                    </p>
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground">Find the sale price or purchase price needed to hit your target.</p>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">What are you targeting?</label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {[
                      { key: 'margin', label: 'Profit Margin %' },
                      { key: 'coc', label: 'Cash-on-Cash Return %' },
                      { key: 'profit', label: 'Profit Amount' },
                    ].map(opt => (
                      <button
                        key={opt.key}
                        onClick={() => setGoalType(opt.key)}
                        className={`px-2.5 py-1 rounded-sm text-[11px] font-medium border transition-colors ${goalType === opt.key ? 'bg-[#1A2F23] text-white border-[#1A2F23]' : 'bg-white text-muted-foreground border-border hover:bg-muted/50'}`}
                        data-testid={`goal-type-${opt.key}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                {goalType === 'coc' && optionATiers.length > 1 && (
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Which tier to solve against?</label>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {optionATiers.map((t, i) => (
                        <button
                          key={`tier-btn-${i}-${t.investor_split}`}
                          onClick={() => setGoalTierIndex(i)}
                          className={`px-2.5 py-1 rounded-sm text-[11px] font-medium border transition-colors ${goalTierIndex === i ? 'bg-[#1A2F23] text-white border-[#1A2F23]' : 'bg-white text-muted-foreground border-border hover:bg-muted/50'}`}
                          data-testid={`goal-tier-${i}`}
                        >
                          Tier {i + 1} ({t.investor_split}%)
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  {goalType === 'profit' ? (
                    <CurrencyInput label="Target Profit" value={goalValue} onChange={setGoalValue} />
                  ) : (
                    <PercentInput
                      label={goalType === 'margin' ? 'Target Margin %' : 'Target Cash-on-Cash Return %'}
                      value={goalValue}
                      onChange={setGoalValue}
                    />
                  )}
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Solve for</label>
                  <div className="flex gap-1.5 mt-1.5">
                    {[
                      { key: 'sale_price', label: 'Required Sale Price' },
                      { key: 'purchase_price', label: 'Max Purchase Price' },
                    ].map(opt => (
                      <button
                        key={opt.key}
                        onClick={() => setSolveFor(opt.key)}
                        className={`px-2.5 py-1 rounded-sm text-[11px] font-medium border transition-colors ${solveFor === opt.key ? 'bg-[#C68E5D] text-white border-[#C68E5D]' : 'bg-white text-muted-foreground border-border hover:bg-muted/50'}`}
                        data-testid={`solve-for-${opt.key}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                {goalResult && (
                  <div className={`mt-2 p-3 rounded-sm border-2 ${goalResult.error ? 'border-red-200 bg-red-50' : 'border-[#1A2F23]/20 bg-[#1A2F23]/5'}`}>
                    {goalResult.error ? (
                      <p className="text-xs text-red-600 font-medium">{goalResult.error}</p>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{goalResult.label}</p>
                            <p className="text-xl font-bold font-mono text-[#1A2F23] mt-0.5" data-testid="goal-result-value">{fmt(goalResult.value)}</p>
                          </div>
                          <button
                            onClick={applyGoalResult}
                            className="px-3 py-1.5 rounded-sm text-[11px] font-medium bg-[#1A2F23] text-white hover:bg-[#2a4a38] transition-colors"
                            data-testid="goal-apply-btn"
                          >
                            Apply to Deal
                          </button>
                        </div>
                        {goalSummary && (
                          <div className="pt-2 border-t border-[#1A2F23]/10 grid grid-cols-2 gap-x-4 gap-y-1">
                            <div className="flex justify-between text-[11px]"><span className="text-muted-foreground">Gross Profit</span><span className="font-mono font-medium">{fmt(goalSummary.gp)}</span></div>
                            <div className="flex justify-between text-[11px]"><span className="text-muted-foreground">Margin</span><span className="font-mono font-medium">{fmtPct(goalSummary.margin)}</span></div>
                            <div className="flex justify-between text-[11px]"><span className="text-muted-foreground">LF Profit</span><span className="font-mono font-medium">{fmt(goalSummary.lfProfit)}</span></div>
                            <div className="flex justify-between text-[11px]"><span className="text-muted-foreground">Partner Profit</span><span className="font-mono font-medium">{fmt(goalSummary.partnerProfit)}</span></div>
                            <div className="flex justify-between text-[11px]"><span className="text-muted-foreground">Partner CoC</span><span className="font-mono font-medium">{fmtPct(goalSummary.coc)}</span></div>
                            <div className="flex justify-between text-[11px]"><span className="text-muted-foreground">Partner Inv.</span><span className="font-mono font-medium">{fmt(goalSummary.partnerInv)}</span></div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Section>
          </div>

          {/* RIGHT: Results */}
          <div className="space-y-3">
            <Card className="rounded-sm border-2 border-[#1A2F23]/20">
              <CardHeader className="py-2.5 px-4 bg-[#1A2F23] text-white rounded-t-sm">
                <CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Financial Summary</CardTitle>
              </CardHeader>
              <CardContent className="px-4 py-3 space-y-0.5">
                {isSubdivide && parcelResults.length > 0 && (
                  <div className="mb-2 p-1.5 rounded-sm bg-blue-50/80 border border-blue-100">
                    <p className="text-[9px] text-blue-700 leading-tight">Below reflects the single-sale scenario. See <strong>Subdivide Summary</strong> for per-parcel totals.</p>
                  </div>
                )}
                <StatLine label="Purchase Price" value={fmt(calcs.pp)} />
                <StatLine label="Total Due Diligence" value={fmt(calcs.totalDD)} />
                <StatLine label="Purchase Closing" value={fmt(calcs.purchaseClosing)} />
                <StatLine label="Improvements" value={fmt(calcs.improvements)} />
                <StatLine label="Total Acquisition Cost" value={fmt(calcs.totalAcquisitionCost)} bold />
                <Separator className="my-1" />
                <StatLine label="LF Investment" value={fmt(calcs.lfTotalInvestment)} />
                <StatLine label="Partner Investment" value={fmt(calcs.investorTotalInvestment)} bold />
                <Separator className="my-1" />
                <StatLine label="Sale Price" value={fmt(calcs.sp)} />
                <StatLine label="Sale Closing" value={`-${fmt(calcs.saleClosing)}`} />
                <StatLine label={`Commission (${calcs.saleCommPct}%)`} value={`-${fmt(calcs.saleCommission)}`} />
                <StatLine label="Gross Sales Revenue" value={fmt(calcs.grossSalesRevenue)} bold />
                <Separator className="my-1" />
                <StatLine label="Gross Profit" value={fmt(calcs.grossProfit)} accent bold />
                <StatLine label="Margin" value={fmtPct(calcs.margin)} />
                {(parseFloat(acreage) || 0) > 0 && (
                  <>
                    <Separator className="my-1" />
                    {calcs.pp > 0 && <StatLine label="Purchase $/Acre" value={fmt(calcs.pricePerAcrePurchase)} />}
                    {calcs.sp > 0 && <StatLine label="Sale $/Acre" value={fmt(calcs.pricePerAcreSale)} />}
                  </>
                )}
              </CardContent>
            </Card>

            {isSubdivide && parcelResults.length > 0 && (
              <Card className="rounded-sm border-2 border-[#C68E5D]/30">
                <CardHeader className="py-2.5 px-4 bg-[#C68E5D] text-white rounded-t-sm">
                  <CardTitle className="text-sm flex items-center gap-2"><Layers className="w-4 h-4" /> Subdivide Summary</CardTitle>
                </CardHeader>
                <CardContent className="px-4 py-3 space-y-0.5">
                  <StatLine label="Total Parcels" value={String(parcelResults.length)} />
                  <StatLine label="Total Acres" value={subdivSummary?.totalAcres?.toFixed(2) || '0'} />
                  <StatLine label="Total Sale Revenue" value={fmt(subdivSummary?.totalRevenue || 0)} />
                  <StatLine label="Total Closing Costs" value={fmt(subdivSummary?.totalClosing || 0)} />
                  <Separator className="my-1" />
                  <StatLine label="Total Gross Profit" value={fmt(subdivSummary?.totalGP || 0)} accent bold />
                  <StatLine label="Total LF Profit" value={fmt(subdivSummary?.totalLF || 0)} bold />
                  <StatLine label="Total Partner Profit" value={fmt(subdivSummary?.totalPartner || 0)} />
                  <Separator className="my-1" />
                  {parcelResults.map((r, i) => (
                    <div key={`${r.name || ''}-${i}`} className="flex justify-between text-[11px] py-0.5">
                      <span className="text-muted-foreground">{r.name} {r.is_jv ? '' : '(No JV)'}</span>
                      <span className="font-mono">{fmt(r.grossProfit)} <span className="text-muted-foreground ml-1">({r.tierLabel})</span></span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {isSubdivide && parcelResults.length > 0 && parcelResultsB.length > 0 && (
              <Card className="rounded-sm border-2 border-[#1A2F23]/15" data-testid="pub-scenario-comparison">
                <CardHeader className="py-2.5 px-4 bg-gradient-to-r from-[#1A2F23] to-[#C68E5D] text-white rounded-t-sm">
                  <CardTitle className="text-sm flex items-center gap-2"><Columns className="w-4 h-4" /> Scenario Comparison</CardTitle>
                </CardHeader>
                <CardContent className="px-0 py-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px]">
                      <thead className="bg-muted/40 text-muted-foreground">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Parcel</th>
                          <th className="text-right px-2 py-2 font-medium">Acres</th>
                          <th className="text-right px-2 py-2 font-medium">Days</th>
                          <th className="text-center px-2 py-2 font-medium border-l bg-[#1A2F23]/5" colSpan="3">Scenario A</th>
                          <th className="text-center px-2 py-2 font-medium border-l bg-[#C68E5D]/10" colSpan="3">Scenario B</th>
                        </tr>
                        <tr className="border-t text-[10px]">
                          <th colSpan="3"></th>
                          <th className="text-right px-2 py-1 font-medium border-l bg-[#1A2F23]/5">Tier</th>
                          <th className="text-right px-2 py-1 font-medium bg-[#1A2F23]/5">LF Profit</th>
                          <th className="text-right px-2 py-1 font-medium bg-[#1A2F23]/5">Partner</th>
                          <th className="text-right px-2 py-1 font-medium border-l bg-[#C68E5D]/10">Tier</th>
                          <th className="text-right px-2 py-1 font-medium bg-[#C68E5D]/10">LF Profit</th>
                          <th className="text-right px-2 py-1 font-medium bg-[#C68E5D]/10">Partner</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parcelResults.map((rA, i) => {
                          const rB = parcelResultsB[i];
                          return (
                            <tr key={`${rA.name || ''}-${i}`} className="border-t">
                              <td className="px-3 py-1.5 font-medium">{rA.name}{!rA.is_jv ? ' (No JV)' : ''}</td>
                              <td className="px-2 py-1.5 text-right font-mono">{rA.pAcres}</td>
                              <td className="px-2 py-1.5 text-right font-mono">{rA.days_held || '—'}</td>
                              <td className="px-2 py-1.5 text-right font-mono text-muted-foreground border-l bg-[#1A2F23]/5">{rA.tierLabel}</td>
                              <td className="px-2 py-1.5 text-right font-mono bg-[#1A2F23]/5">{fmt(rA.lfProfit)}</td>
                              <td className="px-2 py-1.5 text-right font-mono bg-[#1A2F23]/5">{fmt(rA.partnerProfit)}</td>
                              <td className="px-2 py-1.5 text-right font-mono text-muted-foreground border-l bg-[#C68E5D]/10">{rB?.tierLabel || '—'}</td>
                              <td className="px-2 py-1.5 text-right font-mono bg-[#C68E5D]/10">{fmt(rB?.lfProfit || 0)}</td>
                              <td className="px-2 py-1.5 text-right font-mono bg-[#C68E5D]/10">{fmt(rB?.partnerProfit || 0)}</td>
                            </tr>
                          );
                        })}
                        <tr className="border-t-2 font-bold bg-muted/30">
                          <td className="px-3 py-2" colSpan="3">Totals</td>
                          <td className="px-2 py-2 border-l bg-[#1A2F23]/5"></td>
                          <td className="px-2 py-2 text-right font-mono bg-[#1A2F23]/5">{fmt(subdivSummary?.totalLF || 0)}</td>
                          <td className="px-2 py-2 text-right font-mono bg-[#1A2F23]/5">{fmt(subdivSummary?.totalPartner || 0)}</td>
                          <td className="px-2 py-2 border-l bg-[#C68E5D]/10"></td>
                          <td className="px-2 py-2 text-right font-mono bg-[#C68E5D]/10">{fmt(subdivSummaryB?.totalLF || 0)}</td>
                          <td className="px-2 py-2 text-right font-mono bg-[#C68E5D]/10">{fmt(subdivSummaryB?.totalPartner || 0)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  {subdivSummary && subdivSummaryB && (
                    <div className="px-4 py-2.5 border-t bg-muted/20 flex items-center justify-between text-xs">
                      <span className="text-muted-foreground font-medium">LF Profit Advantage:</span>
                      <span className={`font-mono font-bold ${(subdivSummary.totalLF - subdivSummaryB.totalLF) >= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {(subdivSummary.totalLF - subdivSummaryB.totalLF) >= 0 ? 'Scenario A' : 'Scenario B'} by {fmt(Math.abs(subdivSummary.totalLF - subdivSummaryB.totalLF))}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <ResultsTable results={optionAResults} title="Scenario A — Partner & LF Returns" investorCapital={calcs.investorTotalInvestment} />
            <ResultsTable results={optionBResults} title="Scenario B — Partner & LF Returns" investorCapital={calcs.investorTotalInvestment} />

            {compareCalcs && compareAnalysis && (
              <Card className="rounded-sm border-2 border-amber-300/50">
                <CardHeader className="py-2.5 px-4 bg-amber-50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2"><Columns className="w-4 h-4" /> Compare: {compareAnalysis.name}</CardTitle>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setCompareId(null)}><X className="w-3 h-3" /></Button>
                  </div>
                </CardHeader>
                <CardContent className="px-4 py-3 text-xs">
                  <div className="grid grid-cols-3 gap-2 font-medium text-muted-foreground mb-1.5">
                    <span></span><span className="text-right">Current</span><span className="text-right">Compare</span>
                  </div>
                  {[
                    ['Purchase Price', calcs.pp, compareCalcs.pp],
                    ['Sale Price', calcs.sp, compareCalcs.sp],
                    ['Gross Profit', calcs.grossProfit, compareCalcs.gp],
                    ['LF Investment', calcs.lfTotalInvestment, compareCalcs.lfInv],
                    ['Partner Investment', calcs.investorTotalInvestment, compareCalcs.invInv],
                  ].map(([label, curr, comp]) => (
                    <div key={label} className="grid grid-cols-3 gap-2 items-center py-1 border-b last:border-0">
                      <span className="text-muted-foreground">{label}</span>
                      <span className="text-right font-mono">{fmt(curr)}</span>
                      <span className="text-right font-mono">{fmt(comp)}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Saved Dialog */}
      <Dialog open={showSaved} onOpenChange={setShowSaved}>
        <DialogContent className="sm:max-w-lg max-h-[70vh] overflow-y-auto" data-testid="pub-saved-dialog">
          <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><FolderOpen className="w-5 h-5" /> Saved Analyses</DialogTitle></DialogHeader>
          {analyses.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No saved analyses yet. Create one and click Save.</p>
          ) : (
            <div className="space-y-2">
              {analyses.map(a => {
                const d = a.deal || {};
                return (
                  <div key={a.id} className={`border rounded-sm p-3 hover:bg-muted/30 transition-colors ${a.id === activeId ? 'border-[#1A2F23] bg-[#1A2F23]/5' : ''}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => loadAnalysis(a)}>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{a.name}</span>
                          {a.id === activeId && <Badge className="text-[9px] bg-[#1A2F23] text-white">Active</Badge>}
                        </div>
                        <div className="flex gap-3 text-[11px] text-muted-foreground mt-0.5">
                          <span>Buy: {fmt(parseFloat(d.purchase_price) || 0)}</span>
                          <span>Sell: {fmt(parseFloat(d.sale_price) || 0)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Compare" onClick={() => { setCompareId(a.id); setShowSaved(false); }}><Columns className="w-3 h-3" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Duplicate" onClick={() => handleDuplicate(a.id)}><Copy className="w-3 h-3" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" title="Delete" onClick={() => handleDelete(a.id)}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <HowToUseGuide open={showGuide} onOpenChange={setShowGuide} />
    </div>
  );
}
